/**
 * The LEGACY Tickets group: a ticket-first lens over the same conversation
 * queue, so it lives in the Inbox panel rather than owning a rail area of its
 * own.
 *
 * It is superseded by the ticket engine's own queue (`cases.js`), which now
 * owns the "Tickets" name. Wherever the engine's flag is on this group is not
 * built at all — two rows called Tickets would be a coin toss, and the engine
 * is the one people are meant to work in. With the engine off, the lens stays
 * exactly as it was.
 *
 * The views (All, Unassigned, My open, one per state) are URL query params on
 * the single `tickets_index` route - see `conversationLens.js` - so every child here
 * is a deep link that survives a reload. All children share one path and
 * differ only by query, which is why each declares `activeQuery`: the subset
 * of the route query that identifies it for active-state matching.
 *
 * The three primary views stay visible; the per-state views sit behind the
 * "More" fold, mirroring how Conversations folds Participating.
 */
import { h } from 'vue';

import { TICKET_VIEWS } from 'dashboard/helper/conversationLens';
import ViewLeaf from '../ViewLeaf.vue';

/**
 * The single reference to the ticket count getters. The counts layer owns both
 * (see `conversationUnreadCounts`), so nothing else in the sidebar names them:
 * `useSidebarNavigation` imports `perView` from here, and the group header
 * reads `header` through `getterKeys`.
 *
 * These are the TOTAL counts, not the unread ones. Tickets is a queue people
 * work through rather than a stream they catch up on, so the useful number is
 * how many are sitting in the view - a fully-read backlog of 40 open tickets
 * is not an empty badge. The unread tally still ships on the same payload
 * (`getTicketViewUnreadCount`) for anything that wants "needs attention".
 */
export const TICKET_COUNT_GETTERS = {
  header: 'conversationUnreadCounts/getTicketsTotalCount',
  perView: 'conversationUnreadCounts/getTicketViewTotalCount',
};

// Those totals are a backlog, so the badge must not announce them as unread.
const TICKET_COUNT_LABEL_KEY = 'SIDEBAR.TICKET_COUNT';

export default ({
  t,
  accountScopedRoute,
  hasTickets,
  hasSupportCases,
  showTicketsMore,
  toggleTicketsMore,
  ticketCustomViews = [],
  currentUserId,
  isAdmin,
  onDeleteTicketView,
  onToggleTicketViewShared,
  // Undefined before the counts request resolves; an undefined count renders
  // nothing.
  getTicketViewCount,
}) => {
  // The engine owns the name wherever it is on, so the lens steps aside.
  if (!hasTickets || hasSupportCases) return null;

  return {
    name: 'Tickets',
    label: t('SIDEBAR.TICKETS'),
    icon: 'i-ph-ticket',
    getterKeys: { count: TICKET_COUNT_GETTERS.header },
    // Every ticket badge is a backlog total, so none of them announce "unread".
    badgeCountLabelKey: TICKET_COUNT_LABEL_KEY,
    children: [
      ...TICKET_VIEWS.map(view => ({
        name: `Tickets ${view.key}`,
        label: t(view.labelKey),
        icon: view.icon,
        badgeCount: getTicketViewCount?.(view.key) ?? 0,
        badgeCountLabelKey: TICKET_COUNT_LABEL_KEY,
        to: accountScopedRoute('tickets_index', {}, { view: view.key }),
        activeQuery: { view: view.key },
        // The open ticket lives at `tickets/:id`, so the view stays lit
        // beneath it.
        activePathPrefix: true,
        // State views are always built (the collapsed rail and active-route
        // matching need them); the fold only hides them in the expanded list.
        ...(view.primary ? {} : { folded: !showTicketsMore }),
      })),
      // Saved views (CustomFilter records carrying `context: 'tickets'`)
      // arrive position-sorted from the store. Their leaf body swaps in the
      // shared ViewLeaf for the ⋯ manage menu; owners manage their own views,
      // admins additionally manage (and share/unshare) shared ones.
      ...ticketCustomViews.map(view => ({
        name: `Tickets view ${view.id}`,
        label: view.name,
        icon: view.shared ? 'i-lucide-users' : 'i-lucide-bookmark',
        to: accountScopedRoute(
          'tickets_index',
          {},
          { custom_view: String(view.id) }
        ),
        activeQuery: { custom_view: String(view.id) },
        activePathPrefix: true,
        component: leafProps =>
          h(ViewLeaf, {
            ...leafProps,
            shared: Boolean(view.shared),
            canManage:
              view.user_id === currentUserId ||
              (Boolean(view.shared) && Boolean(isAdmin)),
            canShare: Boolean(isAdmin),
            onDelete: () => onDeleteTicketView(view),
            onToggleShared: () => onToggleTicketViewShared(view),
          }),
      })),
      {
        name: 'Tickets More',
        label: showTicketsMore
          ? t('SIDEBAR.SHOW_LESS')
          : t('SIDEBAR.SHOW_MORE'),
        icon: showTicketsMore ? 'i-ph-caret-up' : 'i-ph-caret-down',
        onSelect: toggleTicketsMore,
      },
    ],
  };
};
